import { useQuery } from "@tanstack/react-query"
import {
  BookOpen,
  ChevronRight,
  CircleCheck,
  ExternalLink,
  Pause,
  Play,
  Printer,
  Search,
  Square,
  Volume2,
} from "lucide-react"
import { useMemo, useState } from "react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import { FLUJOS } from "@/componentes/guia/contenido"
import { decir, hayVoz, useLectura } from "@/componentes/guia/lectura"
import { Replica } from "@/componentes/guia/Replica"
import type { Flujo } from "@/componentes/guia/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"

const CLAVE_HECHOS = "guia:hechos"

function leerHechos(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CLAVE_HECHOS) ?? "[]") as string[])
  } catch {
    return new Set()
  }
}

function guardarHechos(h: Set<string>) {
  try {
    localStorage.setItem(CLAVE_HECHOS, JSON.stringify([...h]))
  } catch {
    // Sin almacenamiento (ventana privada): el avance dura lo que la pestaña.
  }
}

/** Todo el texto de un flujo, en minúsculas y sin tildes, para buscar. */
function textoDe(f: Flujo): string {
  return [
    f.titulo,
    f.cuando,
    f.resultado,
    f.ojo ?? "",
    ...f.pasos.flatMap((p) => [p.titulo, p.texto, ...(p.elementos ?? []).map(decir)]),
  ]
    .join(" ")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
}

/**
 * La guía de uso del sistema (28/10, pedido del usuario con la estructura nueva): cada
 * función, paso a paso, con la pantalla replicada, el botón para ir a ella y la lectura en
 * voz alta. Basada en la guía de Reyes del Águila y mejorada: contenido como datos
 * (`componentes/guia/contenido.ts`), filtro por departamento —el mismo orden del menú—,
 * avance de cada quien y voz.
 */
export function Guia() {
  const [buscar, setBuscar] = useState("")
  const [depto, setDepto] = useState<string | null>(null)
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set())
  const [hechos, setHechos] = useState<Set<string>>(leerHechos)
  const [velocidad, setVelocidad] = useState(1)
  const voz = useLectura(velocidad)
  const conVoz = hayVoz()

  const secciones = useQuery({
    queryKey: ["menu", "secciones"],
    queryFn: () => api.get<{ orden: string[]; sin_modulo: string[] }>("/api/v1/menu/secciones"),
    staleTime: 5 * 60_000,
  })

  const indice = useMemo(() => new Map(FLUJOS.map((f) => [f.id, textoDe(f)])), [])
  const orden = secciones.data?.orden ?? []
  const departamentos = [...new Set(FLUJOS.map((f) => f.departamento))].sort(
    (a, b) => (orden.indexOf(a) + 1 || 99) - (orden.indexOf(b) + 1 || 99) || a.localeCompare(b),
  )
  const consulta = buscar
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
  const visibles = FLUJOS.filter(
    (f) =>
      (depto === null || f.departamento === depto) &&
      (consulta === "" || (indice.get(f.id) ?? "").includes(consulta)),
  )

  const alternar = (id: string) =>
    setAbiertos((a) => {
      const n = new Set(a)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const marcar = (id: string) =>
    setHechos((h) => {
      const n = new Set(h)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      guardarHechos(n)
      return n
    })

  const pct = Math.round((hechos.size / FLUJOS.length) * 100)

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <BookOpen size={20} aria-hidden={true} /> Guía de uso
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Cada función del sistema paso a paso, con la pantalla de ejemplo. Puedes escucharla
            mientras lo haces.
          </p>
        </div>
        <Boton className="no-print" onClick={() => window.print()}>
          <Printer size={16} aria-hidden={true} /> Imprimir
        </Boton>
      </header>

      <div className="tarjeta space-y-3 p-4 no-print">
        <label className="flex items-center gap-2">
          <Search size={16} aria-hidden={true} />
          <span className="sr-only">Buscar en la guía</span>
          <input
            className="campo flex-1"
            placeholder="¿Qué quieres hacer? Por ejemplo: aprobar una orden"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </label>
        <fieldset className="flex flex-wrap gap-1">
          <legend className="sr-only">Departamento</legend>
          {[null, ...departamentos].map((d) => (
            <button
              key={d ?? "todos"}
              type="button"
              aria-pressed={depto === d}
              onClick={() => setDepto(d)}
              className={`foco-cristal rounded-[var(--radius-control)] px-2.5 py-1 text-xs ${depto === d ? "activo-cristal" : "toque-cristal"}`}
            >
              {d ?? "Todos"}
            </button>
          ))}
        </fieldset>
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="flex items-center gap-2" style={{ color: "var(--fg-muted)" }}>
            <span
              aria-hidden={true}
              className="inline-block h-1.5 w-24 overflow-hidden rounded-full"
              style={{ background: "var(--cristal-relleno)" }}
            >
              <span
                className="block h-full"
                style={{ width: `${pct}%`, background: "var(--success)" }}
              />
            </span>
            Has hecho {hechos.size} de {FLUJOS.length} ({pct} %)
          </span>
          {conVoz && (
            <label className="flex items-center gap-2">
              <Volume2 size={14} aria-hidden={true} />
              Velocidad de la voz
              <select
                className="campo h-7 py-0 text-xs"
                value={velocidad}
                onChange={(e) => setVelocidad(Number(e.target.value))}
              >
                <option value={0.85}>Pausada</option>
                <option value={1}>Normal</option>
                <option value={1.2}>Rápida</option>
              </select>
            </label>
          )}
        </div>
      </div>

      {visibles.length === 0 && (
        <p className="tarjeta p-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          Nada coincide con «{buscar}». Prueba con otra palabra, o quita el filtro de departamento.
        </p>
      )}

      <ul className="space-y-3">
        {visibles.map((f) => {
          const abierto = abiertos.has(f.id) || consulta !== ""
          const hecho = hechos.has(f.id)
          const suena = voz.leyendo === f.id
          return (
            <li key={f.id} className="tarjeta p-0">
              <button
                type="button"
                aria-expanded={abierto}
                onClick={() => alternar(f.id)}
                className="foco-cristal flex w-full items-start gap-3 p-4 text-left"
              >
                <ChevronRight
                  size={16}
                  aria-hidden={true}
                  className="mt-0.5 shrink-0 transition-transform motion-reduce:transition-none"
                  style={{ transform: abierto ? "rotate(90deg)" : undefined }}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{f.titulo}</span>
                    {hecho && (
                      <Insignia tono="exito" icono={<CircleCheck size={12} aria-hidden={true} />}>
                        Hecho
                      </Insignia>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {f.departamento} · {f.cuando}
                  </span>
                </span>
              </button>

              {abierto && (
                <div
                  className="space-y-4 border-t px-4 pt-3 pb-4"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="flex flex-wrap items-center gap-2 no-print">
                    {conVoz &&
                      (suena ? (
                        <>
                          <Boton tamano="sm" onClick={voz.alternarPausa}>
                            {voz.pausado ? <Play size={14} /> : <Pause size={14} />}
                            {voz.pausado ? "Seguir" : "Pausar"}
                          </Boton>
                          <Boton tamano="sm" onClick={voz.parar}>
                            <Square size={14} /> Detener
                          </Boton>
                        </>
                      ) : (
                        <Boton tamano="sm" variante="primario" onClick={() => voz.leer(f)}>
                          <Volume2 size={14} /> Escuchar
                        </Boton>
                      ))}
                    {f.ruta && (
                      <Link
                        to={f.ruta}
                        className="boton-secundario inline-flex h-8 items-center gap-1.5 px-3 text-xs foco-cristal"
                      >
                        <ExternalLink size={14} aria-hidden={true} /> Ir a la pantalla
                      </Link>
                    )}
                    <label className="ml-auto flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={hecho} onChange={() => marcar(f.id)} />
                      Ya lo hice
                    </label>
                  </div>

                  {(f.antes?.length || f.permiso) && (
                    <div className="space-y-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                      {f.antes?.length ? <p>Antes: {f.antes.join(" · ")}.</p> : null}
                      {f.permiso && (
                        <p>
                          Pide el permiso <code>{f.permiso}</code>. Si no ves el botón, pídelo a
                          quien administra los roles.
                        </p>
                      )}
                    </div>
                  )}

                  <ol className="space-y-3">
                    {f.pasos.map((p, i) => {
                      const ahora = suena && voz.actual === i
                      return (
                        <li
                          key={p.titulo}
                          className={`rounded-[var(--radius-control)] p-3 ${ahora ? "activo-cristal" : ""}`}
                          aria-current={ahora ? "step" : undefined}
                        >
                          <p className="text-sm font-semibold">
                            <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                              {i + 1}.
                            </span>{" "}
                            {p.titulo}
                          </p>
                          {p.texto && <p className="mt-1 text-sm">{p.texto}</p>}
                          {p.elementos && p.elementos.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-end gap-3">
                              {p.elementos.map((e, j) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: contenido fijo de la guía; nunca se reordena.
                                <Replica key={`${e.tipo}-${j}`} e={e} />
                              ))}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ol>

                  <p
                    className={`rounded-[var(--radius-control)] p-3 text-sm ${suena && voz.actual === f.pasos.length ? "activo-cristal" : ""}`}
                  >
                    <strong>Resultado:</strong> {f.resultado}
                  </p>
                  {f.ojo && (
                    <p className="text-sm" style={{ color: "var(--warning)" }}>
                      <strong>Importante:</strong> {f.ojo}
                    </p>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
